import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Typography, Tabs, Tab, Snackbar, Alert, Drawer, IconButton, Tooltip, Chip,
  Divider, Stack, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  History as HistoryIcon, Close as CloseIcon,
  Print as PrintIcon, Delete as DeleteIcon, Visibility as ViewIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';

import { ReportType, ReportFiltersState, REPORT_TYPE_META, PERIOD_PRESETS, formatCurrency, formatDate, formatNumber, formatPct } from './reports/types';
import { FiltersBar, exportToCsv } from './reports/components';
import {
  OverviewSection, FinancialSection, CashflowSection, ReceivablesSection,
  AppointmentsSection, ProductivitySection, PatientsSection, ProceduresSection,
  BudgetsSection, CommissionsSection, InventorySection, InsuranceSection, LoadingSection,
} from './reports/sections';

const TAB_ORDER: ReportType[] = ['overview', 'financial', 'cashflow', 'receivables', 'appointments', 'productivity', 'patients', 'procedures', 'budgets', 'commissions', 'inventory', 'insurance'];

const ENDPOINT_MAP: Record<ReportType, string> = {
  overview: '/clinic/reports/overview',
  financial: '/clinic/reports/financial',
  cashflow: '/clinic/reports/cashflow',
  receivables: '/clinic/reports/receivables',
  appointments: '/clinic/reports/appointments',
  productivity: '/clinic/reports/productivity',
  patients: '/clinic/reports/demographics',
  procedures: '/clinic/reports/procedures',
  budgets: '/clinic/reports/budgets',
  commissions: '/clinic/reports/commissions',
  inventory: '/clinic/reports/inventory',
  insurance: '/clinic/reports/insurance',
};

// Tipos persistidos no banco de relatórios usam alguns aliases:
const SAVED_TYPE_MAP: Record<ReportType, string> = {
  overview: 'overview',
  financial: 'financial',
  cashflow: 'cashflow',
  receivables: 'receivables',
  appointments: 'appointments',
  productivity: 'professionals',
  patients: 'patients',
  procedures: 'procedures',
  budgets: 'budgets',
  commissions: 'commissions',
  inventory: 'inventory',
  insurance: 'insurance',
};

function getInitialFilters(): ReportFiltersState {
  const preset = PERIOD_PRESETS.find(p => p.id === 'this_month')!;
  const r = preset.resolve();
  return { preset: 'this_month', from: r.from, to: r.to, unitId: null, unitMode: 'current', compare: false, registrarUserId: '' };
}

function userCanPickReportRegistrar(): boolean {
  try {
    const raw = localStorage.getItem('auth_user');
    if (!raw) return false;
    const u = JSON.parse(raw);
    const role = String(u?.role || '').toLowerCase();
    return role === 'admin_master' || role === 'admin';
  } catch { return false; }
}

/** Relatórios que usam transações financeiras por `registered_by_system_user_id`. */
const REPORTS_WITH_REGISTRAR_FILTER: ReportType[] = ['overview', 'financial', 'cashflow', 'receivables', 'productivity', 'insurance'];

function userCanSeeAllUnits(): boolean {
  try {
    const raw = localStorage.getItem('auth_user');
    if (!raw) return false;
    const u = JSON.parse(raw);
    const ids = (u?.available_units || u?.allowed_units || []).map((x: any) => x?.id || x).filter(Boolean);
    const role = String(u?.role || '').toLowerCase();
    return ids.length > 1 && (role === 'admin' || role === 'super_admin' || role === 'owner');
  } catch { return false; }
}

const Reports: React.FC = () => {
  const { isMobile } = useClinicPageBreakpoints();
  const [tab, setTab] = useState<ReportType>('overview');
  const [filters, setFilters] = useState<ReportFiltersState>(getInitialFilters);
  const [data, setData] = useState<Record<ReportType, any>>({} as any);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' | 'info' });
  const [savedOpen, setSavedOpen] = useState(false);
  const [savedReports, setSavedReports] = useState<any[]>([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [saveDialog, setSaveDialog] = useState(false);
  const [saveForm, setSaveForm] = useState({ title: '', description: '' });
  const [saving, setSaving] = useState(false);
  const requestSeqRef = useRef(0);

  const canSeeAllUnits = useMemo(() => userCanSeeAllUnits(), []);
  const canPickRegistrar = useMemo(() => userCanPickReportRegistrar(), []);
  const [registrarOptions, setRegistrarOptions] = useState<{ id: string; full_name?: string | null; email?: string | null }[]>([]);

  const loadRegistrars = useCallback(async () => {
    if (!canPickRegistrar) return;
    try {
      const params: Record<string, string> = {};
      if (canSeeAllUnits && filters.unitMode === 'all') params.unit_id = 'all';
      const res = await axios.get(getApiUrl('/clinic/reports/unit-registrars'), { params });
      setRegistrarOptions((res.data?.data || []).map((u: any) => ({ id: u.id, full_name: u.full_name, email: u.email })));
    } catch {
      setRegistrarOptions([]);
    }
  }, [canPickRegistrar, canSeeAllUnits, filters.unitMode]);

  useEffect(() => { void loadRegistrars(); }, [loadRegistrars]);

  // -------------------------------------------------------------------
  // Carrega o relatório atual
  // -------------------------------------------------------------------
  const loadReport = useCallback(async (type: ReportType, opts: { silent?: boolean } = {}) => {
    const seq = ++requestSeqRef.current;
    if (!opts.silent) setLoading(true);
    try {
      const params: any = { date_from: filters.from, date_to: filters.to };
      if (filters.compare) params.compare_to = 'previous';
      const headers: Record<string, string> = {};
      if (canSeeAllUnits && filters.unitMode === 'all') {
        params.unit_id = 'all';
      }
      if (REPORTS_WITH_REGISTRAR_FILTER.includes(type) && filters.registrarUserId) {
        params.registered_by_system_user_id = filters.registrarUserId;
      }
      const res = await axios.get(getApiUrl(ENDPOINT_MAP[type]), { params, headers });
      if (seq !== requestSeqRef.current) return;
      setData(prev => ({ ...prev, [type]: res.data?.data ?? null }));
    } catch (e: any) {
      if (seq !== requestSeqRef.current) return;
      setSnackbar({ open: true, severity: 'error', message: e.response?.data?.error || 'Erro ao carregar relatório' });
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, [filters, canSeeAllUnits]);

  useEffect(() => { loadReport(tab); /* eslint-disable-next-line */ }, [tab, filters.from, filters.to, filters.compare, filters.unitMode, filters.registrarUserId]);

  // Recarrega quando o usuário trocar de unidade
  useEffect(() => {
    const handler = () => loadReport(tab);
    window.addEventListener('unit:changed', handler);
    return () => window.removeEventListener('unit:changed', handler);
  }, [tab, loadReport]);

  // -------------------------------------------------------------------
  // Relatórios salvos
  // -------------------------------------------------------------------
  const loadSaved = useCallback(async () => {
    setSavedLoading(true);
    try {
      const res = await axios.get(getApiUrl('/clinic/reports'), { params: { limit: 100 } });
      setSavedReports(res.data?.data || []);
    } catch {
      setSnackbar({ open: true, severity: 'error', message: 'Erro ao carregar histórico' });
    } finally { setSavedLoading(false); }
  }, []);

  const openSaved = () => { setSavedOpen(true); loadSaved(); };

  const handleDeleteSaved = async (id: string) => {
    if (!window.confirm('Excluir este relatório do histórico?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/reports/${id}`));
      loadSaved();
      setSnackbar({ open: true, severity: 'success', message: 'Removido' });
    } catch {
      setSnackbar({ open: true, severity: 'error', message: 'Erro ao remover' });
    }
  };

  const handleViewSaved = async (id: string, doPrint: boolean) => {
    try {
      const res = await axios.get(getApiUrl(`/clinic/reports/${id}/print`));
      const { report, clinic } = res.data?.data || {};
      const html = buildPrintHtml(report?.title || 'Relatório', report?.report_data || {}, clinic, report?.report_number, report?.date_start, report?.date_end);
      const w = window.open('', '_blank');
      if (!w) { setSnackbar({ open: true, severity: 'warning', message: 'Permita pop-ups para visualizar/imprimir' }); return; }
      w.document.write(html);
      w.document.close();
      if (doPrint) setTimeout(() => { w.print(); }, 400);
    } catch {
      setSnackbar({ open: true, severity: 'error', message: 'Erro ao abrir relatório' });
    }
  };

  // -------------------------------------------------------------------
  // Imprimir relatório atual (sem persistir)
  // -------------------------------------------------------------------
  const printCurrent = () => {
    const reportData = data[tab];
    if (!reportData) { setSnackbar({ open: true, severity: 'warning', message: 'Nada para imprimir' }); return; }
    const meta = REPORT_TYPE_META[tab];
    const html = buildPrintHtml(`Relatório ${meta.label}`, reportData, null, null, filters.from, filters.to);
    const w = window.open('', '_blank');
    if (!w) { setSnackbar({ open: true, severity: 'warning', message: 'Permita pop-ups para imprimir' }); return; }
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.print(); }, 400);
  };

  // -------------------------------------------------------------------
  // Salvar (gera no backend e persiste)
  // -------------------------------------------------------------------
  const openSaveDialog = () => {
    const meta = REPORT_TYPE_META[tab];
    setSaveForm({ title: `Relatório ${meta.label} — ${formatDate(filters.from)} a ${formatDate(filters.to)}`, description: '' });
    setSaveDialog(true);
  };
  const handleSave = async () => {
    setSaving(true);
    try {
      const params: any = filters.compare ? { compare_to: 'previous' } : {};
      if (canSeeAllUnits && filters.unitMode === 'all') params.unit_id = 'all';
      if (canPickRegistrar && filters.registrarUserId && REPORTS_WITH_REGISTRAR_FILTER.includes(tab)) {
        params.registered_by_system_user_id = filters.registrarUserId;
      }
      await axios.post(getApiUrl('/clinic/reports'), {
        type: SAVED_TYPE_MAP[tab],
        title: saveForm.title,
        description: saveForm.description,
        date_start: filters.from,
        date_end: filters.to,
        parameters: params,
      });
      setSnackbar({ open: true, severity: 'success', message: 'Relatório salvo no histórico!' });
      setSaveDialog(false);
    } catch (e: any) {
      setSnackbar({ open: true, severity: 'error', message: e.response?.data?.error || 'Erro ao salvar' });
    } finally { setSaving(false); }
  };

  // -------------------------------------------------------------------
  // Exportar CSV (do snapshot atual, baseado em listas conhecidas)
  // -------------------------------------------------------------------
  const exportCurrent = () => {
    const d = data[tab];
    if (!d) { setSnackbar({ open: true, severity: 'warning', message: 'Carregue o relatório antes de exportar' }); return; }
    const filenameBase = `${REPORT_TYPE_META[tab].label}_${filters.from}_a_${filters.to}`.replace(/[^\w-]+/g, '_');
    switch (tab) {
      case 'financial': {
        const rows = (d.by_payment_method || []).map((r: any) => ({ tipo: 'método', label: r.method, qtd: r.count, valor: r.amount }));
        rows.push(...(d.by_category || []).map((r: any) => ({ tipo: 'categoria', label: r.category, qtd: r.count, valor: r.amount })));
        rows.push(...(d.top_debtors || []).map((r: any) => ({ tipo: 'devedor', label: r.patient_name, qtd: r.count, valor: r.total })));
        exportToCsv(rows, filenameBase);
        break;
      }
      case 'productivity':
        exportToCsv((d.professionals || []), filenameBase);
        break;
      case 'appointments':
        exportToCsv((d.by_professional || []), filenameBase);
        break;
      case 'procedures':
        exportToCsv((d.top_procedures || d.all_procedures || []), filenameBase);
        break;
      case 'cashflow':
        exportToCsv((d.series || []), filenameBase);
        break;
      case 'receivables':
        exportToCsv((d.top_debtors || []), filenameBase);
        break;
      case 'budgets':
        exportToCsv((d.monthly || []), filenameBase);
        break;
      case 'commissions':
        exportToCsv((d.by_professional || []), filenameBase);
        break;
      case 'inventory':
        exportToCsv((d.top_consumed || []), filenameBase);
        break;
      case 'insurance':
        exportToCsv((d.by_insurance || []), filenameBase);
        break;
      case 'patients':
        exportToCsv((d.by_city || []).map((c: any) => ({ tipo: 'cidade', ...c })).concat((d.by_source || []).map((s: any) => ({ tipo: 'origem', ...s }))), filenameBase);
        break;
      case 'overview':
        exportToCsv((d.series || []), filenameBase);
        break;
    }
  };

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------
  const renderActiveSection = () => {
    const props = { data: data[tab], loading };
    switch (tab) {
      case 'overview': return <OverviewSection {...props} />;
      case 'financial': return <FinancialSection {...props} />;
      case 'cashflow': return <CashflowSection {...props} />;
      case 'receivables': return <ReceivablesSection {...props} />;
      case 'appointments': return <AppointmentsSection {...props} />;
      case 'productivity': return <ProductivitySection {...props} />;
      case 'patients': return <PatientsSection {...props} />;
      case 'procedures': return <ProceduresSection {...props} />;
      case 'budgets': return <BudgetsSection {...props} />;
      case 'commissions': return <CommissionsSection {...props} />;
      case 'inventory': return <InventorySection {...props} />;
      case 'insurance': return <InsuranceSection {...props} />;
      default: return null;
    }
  };

  const meta = REPORT_TYPE_META[tab];

  return (
    <Box sx={clinicPageContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>Relatórios e Analytics</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Painel completo com KPIs, gráficos e dados granulares — multi-unidade, comparações e histórico salvo.
          </Typography>
        </Box>
        <Tooltip title="Histórico de relatórios salvos">
          <Button startIcon={<HistoryIcon />} onClick={openSaved} variant="outlined" sx={{ textTransform: 'none', color: '#0f766e', borderColor: '#0f766e', '&:hover': { borderColor: '#0d9488', bgcolor: '#0f766e0a' } }}>
            Histórico
          </Button>
        </Tooltip>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as ReportType)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2,
          minHeight: 40,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 40, fontSize: '.85rem' },
          '& .Mui-selected': { color: `${meta.color} !important` },
          '& .MuiTabs-indicator': { bgcolor: meta.color },
        }}
      >
        {TAB_ORDER.map(t => (
          <Tab key={t} value={t} label={REPORT_TYPE_META[t].label} />
        ))}
      </Tabs>

      <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: meta.color + '0e', border: `1px solid ${meta.color}33` }}>
        <Typography variant="caption" sx={{ color: meta.color, fontWeight: 600 }}>
          {meta.label.toUpperCase()} · {meta.description}
        </Typography>
      </Box>

      <FiltersBar
        state={filters}
        onChange={setFilters}
        onRefresh={() => loadReport(tab)}
        onSave={openSaveDialog}
        onPrint={printCurrent}
        onExport={exportCurrent}
        loading={loading}
        showCompare={tab === 'overview' || tab === 'financial'}
        canSeeAllUnits={canSeeAllUnits}
        showRegistrarFilter={canPickRegistrar && REPORTS_WITH_REGISTRAR_FILTER.includes(tab)}
        registrarOptions={registrarOptions}
      />

      {loading && !data[tab] ? <LoadingSection /> : renderActiveSection()}

      {/* Drawer histórico */}
      <Drawer anchor={isMobile ? 'bottom' : 'right'} open={savedOpen} onClose={() => setSavedOpen(false)} PaperProps={{ sx: { width: isMobile ? '100%' : 480, p: 2 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Histórico</Typography>
          <IconButton onClick={() => setSavedOpen(false)}><CloseIcon /></IconButton>
        </Stack>
        <Divider sx={{ mb: 1 }} />
        {savedLoading ? (
          <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
        ) : savedReports.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', py: 4 }}>Nenhum relatório salvo ainda. Use o botão "Salvar" para gravar um snapshot.</Typography>
        ) : (
          <Stack spacing={1}>
            {savedReports.map(r => (
              <Box key={r.id} sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 2, '&:hover': { borderColor: '#0f766e' } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                      {r.report_number || ''} · {REPORT_TYPE_META[r.report_type as ReportType]?.label || r.report_type}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      {r.date_start && r.date_end ? `${formatDate(r.date_start)} – ${formatDate(r.date_end)}` : '—'} · {r.created_at ? new Date(r.created_at).toLocaleString('pt-BR') : ''}
                    </Typography>
                  </Box>
                  <Stack direction="row">
                    <Tooltip title="Ver"><IconButton size="small" onClick={() => handleViewSaved(r.id, false)} sx={{ color: '#0f766e' }}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Imprimir"><IconButton size="small" onClick={() => handleViewSaved(r.id, true)} sx={{ color: '#0d9488' }}><PrintIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Excluir"><IconButton size="small" onClick={() => handleDeleteSaved(r.id)} sx={{ color: '#ef4444' }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Drawer>

      {/* Dialog salvar */}
      <Dialog open={saveDialog} onClose={() => setSaveDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Salvar relatório</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Os dados atuais serão regenerados no backend e salvos como um documento numerado (REL-…) que você pode imprimir, baixar ou enviar.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip size="small" label={REPORT_TYPE_META[tab].label} sx={{ bgcolor: REPORT_TYPE_META[tab].color + '22', color: REPORT_TYPE_META[tab].color, fontWeight: 700 }} />
              <Chip size="small" label={`${formatDate(filters.from)} – ${formatDate(filters.to)}`} variant="outlined" />
            </Stack>
            <TextField label="Título" value={saveForm.title} onChange={e => setSaveForm({ ...saveForm, title: e.target.value })} fullWidth size="small" />
            <TextField label="Descrição (opcional)" value={saveForm.description} onChange={e => setSaveForm({ ...saveForm, description: e.target.value })} fullWidth size="small" multiline rows={2} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSaveDialog(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} variant="contained" sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none' }}>
            {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

// =====================================================================
// Impressão (HTML estático — A4, profissional, português)
// =====================================================================

const PRINT_CSS = `
  @page { size: A4; margin: 16mm 18mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11.5px; color: #1e293b; margin: 0; background: #fff; }

  /* ── Cabeçalho da clínica ── */
  .clinic-header { display: flex; align-items: flex-start; justify-content: space-between; border-bottom: 3px solid #0f766e; padding-bottom: 10px; margin-bottom: 14px; }
  .clinic-logo { width: 56px; height: 56px; object-fit: contain; }
  .clinic-info-block { flex: 1; padding-left: 12px; }
  .clinic-name { font-size: 17px; font-weight: 800; color: #0d9488; line-height: 1.2; }
  .clinic-sub { font-size: 11px; color: #64748b; margin-top: 2px; }
  .clinic-contact { font-size: 10px; color: #64748b; margin-top: 3px; }
  .report-meta { text-align: right; min-width: 160px; }
  .report-meta .report-type { font-size: 9px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #64748b; }
  .report-meta .report-title { font-size: 14px; font-weight: 700; color: #0f172a; margin: 3px 0 2px; }
  .report-meta .report-period { font-size: 10px; color: #475569; }
  .report-meta .report-number { font-size: 9px; color: #94a3b8; margin-top: 2px; }

  /* ── KPIs ── */
  .kpi-grid { display: grid; gap: 8px; margin: 12px 0 16px; }
  .kpi-grid.cols-4 { grid-template-columns: repeat(4, 1fr); }
  .kpi-grid.cols-3 { grid-template-columns: repeat(3, 1fr); }
  .kpi-grid.cols-2 { grid-template-columns: repeat(2, 1fr); }
  .kpi-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 12px; background: #f8fafc; }
  .kpi-card .kpi-label { font-size: 10px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; }
  .kpi-card .kpi-value { font-size: 15px; font-weight: 800; color: #0f172a; margin-top: 2px; }
  .kpi-card .kpi-sub { font-size: 10px; color: #94a3b8; margin-top: 1px; }
  .kpi-card.green .kpi-value { color: #059669; }
  .kpi-card.red .kpi-value { color: #dc2626; }
  .kpi-card.blue .kpi-value { color: #2563eb; }
  .kpi-card.teal .kpi-value { color: #0d9488; }

  /* ── Seções ── */
  .section { margin-top: 16px; page-break-inside: avoid; }
  .section-header { font-size: 12px; font-weight: 700; color: #0d9488; padding-bottom: 5px; border-bottom: 2px solid #ccfbf1; margin-bottom: 8px; display: flex; justify-content: space-between; align-items: baseline; }
  .section-header small { font-size: 10px; font-weight: 500; color: #64748b; }

  /* ── Tabelas ── */
  table.dt { width: 100%; border-collapse: collapse; font-size: 11px; }
  table.dt thead tr { background: #f0fdf4; }
  table.dt th { padding: 6px 9px; font-weight: 700; color: #374151; border: 1px solid #d1fae5; font-size: 10.5px; text-transform: uppercase; letter-spacing: .04em; }
  table.dt td { padding: 5px 9px; border: 1px solid #e5e7eb; vertical-align: top; }
  table.dt tbody tr:nth-child(even) { background: #fafafa; }
  table.dt tbody tr:hover { background: #f0f9ff; }
  .r { text-align: right; }
  .c { text-align: center; }
  .bold { font-weight: 700; }
  .muted { color: #94a3b8; }
  .green { color: #059669; }
  .red { color: #dc2626; }
  .amber { color: #d97706; }
  .pill { display: inline-block; padding: 1px 7px; border-radius: 99px; font-size: 9.5px; font-weight: 700; }
  .pill-green { background: #dcfce7; color: #15803d; }
  .pill-red { background: #fee2e2; color: #b91c1c; }
  .pill-amber { background: #fef3c7; color: #92400e; }
  .pill-slate { background: #f1f5f9; color: #475569; }
  .pill-blue { background: #dbeafe; color: #1d4ed8; }
  .pill-purple { background: #f3e8ff; color: #6b21a8; }

  /* ── Rodapé ── */
  .print-footer { margin-top: 24px; padding-top: 8px; border-top: 1px dashed #cbd5e1; font-size: 9px; color: #94a3b8; display: flex; justify-content: space-between; }

  @media print {
    .no-print { display: none; }
    .page-break { page-break-before: always; }
    table.dt { font-size: 10px; }
  }
`;

function esc(s: any): string {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function statusPill(status: string): string {
  const map: Record<string, string> = {
    scheduled: 'pill-blue', confirmed: 'pill-green', completed: 'pill-green',
    no_show: 'pill-red', cancelled: 'pill-red', pending: 'pill-amber',
    waiting: 'pill-amber', paid: 'pill-green', partial: 'pill-amber', overdue: 'pill-red',
  };
  const labels: Record<string, string> = {
    scheduled: 'Agendado', confirmed: 'Confirmado', completed: 'Concluído',
    no_show: 'Falta', cancelled: 'Cancelado', pending: 'Pendente',
    waiting: 'Aguardando', paid: 'Pago', partial: 'Parcial', overdue: 'Vencido',
  };
  const cls = map[status] || 'pill-slate';
  const label = labels[status] || status;
  return `<span class="pill ${cls}">${label}</span>`;
}

function buildPrintHtml(title: string, d: any, clinic: any, reportNumber?: string | null, from?: string | null, to?: string | null): string {
  const c = clinic || {};
  const periodStr = from && to ? `${formatDate(from)} a ${formatDate(to)}` : '';
  const clinicName = esc(c?.clinic_name || 'Clínica');
  const clinicSub = esc(c?.clinic_subtitle || '');
  const clinicAddr = [c?.address_street, c?.address_number, c?.address_neighborhood, c?.address_city, c?.address_state].filter(Boolean).map(esc).join(', ');
  const clinicPhone = esc(c?.phone || c?.whatsapp || '');
  const logoUrl = c?.logo_url || '/principal-logo.png';
  const now = new Date().toLocaleString('pt-BR');

  const header = `
    <div class="clinic-header">
      <img class="clinic-logo" src="${logoUrl}" alt="Logo" onerror="this.style.display='none'" />
      <div class="clinic-info-block">
        <div class="clinic-name">${clinicName}</div>
        ${clinicSub ? `<div class="clinic-sub">${clinicSub}</div>` : ''}
        ${clinicAddr || clinicPhone ? `<div class="clinic-contact">${[clinicAddr, clinicPhone].filter(Boolean).join(' &nbsp;·&nbsp; ')}</div>` : ''}
      </div>
      <div class="report-meta">
        <div class="report-type">Relatório Clínico</div>
        <div class="report-title">${esc(title)}</div>
        ${periodStr ? `<div class="report-period">Período: ${periodStr}</div>` : ''}
        ${reportNumber ? `<div class="report-number">${esc(reportNumber)}</div>` : ''}
      </div>
    </div>`;

  const section = (heading: string, html: string, subtitle = '') =>
    `<div class="section">
      <div class="section-header">${heading}${subtitle ? ` <small>${subtitle}</small>` : ''}</div>
      ${html}
    </div>`;

  const kvGrid = (rows: [string, string, string?][], cols: 2|3|4 = 4) =>
    `<div class="kpi-grid cols-${cols}">${rows.map(([label, value, color]) =>
      `<div class="kpi-card${color ? ' '+color : ''}"><div class="kpi-label">${label}</div><div class="kpi-value">${value}</div></div>`
    ).join('')}</div>`;

  const table = (head: string[], rows: (string|number)[][], alignRight?: number[]) => {
    const th = head.map(h => `<th>${h}</th>`).join('');
    const tr = rows.map(row =>
      `<tr>${row.map((v, i) => `<td class="${alignRight?.includes(i) ? 'r' : ''}">${v ?? '—'}</td>`).join('')}</tr>`
    ).join('') || `<tr><td colspan="${head.length}" class="c muted">Sem dados no período</td></tr>`;
    return `<table class="dt"><thead><tr>${th}</tr></thead><tbody>${tr}</tbody></table>`;
  };

  let body = '';

  // ─────────────────────────────────────────────────────
  // RELATÓRIO DE AGENDA — layout detalhado e prioritário
  // ─────────────────────────────────────────────────────
  const isAppointments = !!(d?.summary?.total !== undefined && d?.by_status);
  if (isAppointments) {
    const s = d.summary || {};
    body += kvGrid([
      ['Total de agendamentos', formatNumber(s.total || 0), ''],
      ['Concluídos', `${formatNumber(s.completed || 0)} (${formatPct(s.completion_rate)})`, 'green'],
      ['Faltas', `${formatNumber(s.no_show || 0)} (${formatPct(s.no_show_rate)})`, 'red'],
      ['Cancelados', `${formatNumber(s.cancelled || 0)} (${formatPct(s.cancellation_rate)})`, ''],
    ], 4);

    // Por status
    if (d.by_status?.length) {
      body += section('Distribuição por Status', table(
        ['Status', 'Quantidade', 'Participação'],
        d.by_status.map((r: any) => {
          const total = s.total || 1;
          return [statusPill(r.status), formatNumber(r.count), `${((r.count / total) * 100).toFixed(1)}%`];
        }),
        [1, 2]
      ));
    }

    // Por profissional — tabela completa
    if (d.by_professional?.length) {
      body += section('Atendimentos por Profissional', table(
        ['Profissional', 'Total', 'Concluídos', 'Faltas', 'Cancelados', 'Taxa de Conclusão'],
        d.by_professional.map((p: any) => [
          `<strong>${esc(p.professional_name)}</strong>`,
          formatNumber(p.total ?? p.total_appointments ?? 0),
          formatNumber(p.completed ?? p.completed_appointments ?? 0),
          formatNumber(p.no_show ?? p.no_shows ?? 0),
          formatNumber(p.cancelled ?? 0),
          `<strong>${formatPct(p.completion_rate)}</strong>`,
        ]),
        [1, 2, 3, 4, 5]
      ));
    }

    // Por dia da semana
    if (d.by_day_of_week?.length) {
      body += section('Volume por Dia da Semana', table(
        ['Dia', 'Agendamentos'],
        d.by_day_of_week.map((r: any) => [esc(r.day), formatNumber(r.count)]),
        [1]
      ), '');
    }

    // Por horário
    if (d.by_hour?.length) {
      body += section('Concentração por Horário', table(
        ['Hora', 'Agendamentos'],
        d.by_hour.map((r: any) => [`${String(r.hour).padStart(2, '0')}h`, formatNumber(r.count)]),
        [1]
      ));
    }

    // Listagem detalhada de agendamentos (se disponível)
    const list: any[] = d.appointments_list || d.appointments || d.all_appointments || [];
    if (list.length) {
      const rows = list.map((appt: any) => [
        esc(appt.appointment_date ? formatDate(appt.appointment_date) : (appt.date ? formatDate(appt.date) : '—')),
        `${String(appt.start_time || '').substring(0, 5)} – ${String(appt.end_time || '').substring(0, 5)}`,
        esc(appt.patient_name || appt.clinic_patient?.full_name || '—'),
        esc(appt.professional_name || appt.professional?.full_name || '—'),
        esc(appt.procedure_name || appt.procedure?.name || appt.type || '—'),
        statusPill(appt.status),
        appt.amount != null ? formatCurrency(Number(appt.amount)) : '—',
        esc(appt.notes || appt.obs || ''),
      ]);
      body += `<div class="page-break"></div>` + section(
        'Listagem Detalhada de Agendamentos',
        table(['Data', 'Horário', 'Paciente', 'Profissional', 'Procedimento', 'Status', 'Valor', 'Obs.'], rows, [6]),
        `${rows.length} agendamento(s)`
      );
    }
  }

  // ─────────────────────────────────────────────────────
  // DEMAIS RELATÓRIOS — renderização genérica
  // ─────────────────────────────────────────────────────
  if (!isAppointments) {
    if (d.kpis) {
      body += kvGrid([
        ['Receita realizada', formatCurrency(d.kpis.total_revenue || 0), 'teal'],
        ['Pendente', formatCurrency(d.kpis.total_pending || 0), 'amber'],
        ['Vencido', formatCurrency(d.kpis.total_overdue || 0), 'red'],
        ['Atendimentos', formatNumber(d.kpis.total_appointments || 0), ''],
        ['Concluídos', formatNumber(d.kpis.completed_appointments || 0), 'green'],
        ['Faltas', formatNumber(d.kpis.no_shows || 0), 'red'],
        ['Ticket médio', formatCurrency(d.kpis.avg_ticket || 0), ''],
        ['Pacientes', formatNumber(d.kpis.patients_total || 0), 'blue'],
      ], 4);
    }

    if (d.summary) {
      const s = d.summary;
      const rows: [string, string][] = Object.entries(s).map(([k, v]) => {
        const label = k.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase());
        const isMoney = /amount|revenue|expense|paid|charged|net|ticket|spent|due|overdue/i.test(k);
        const isPct = /rate|pct|percent/i.test(k);
        return [label, isMoney ? formatCurrency(Number(v) || 0) : isPct ? formatPct(v as number) : formatNumber(Number(v) || 0)];
      });
      body += section('Resumo', `<div class="kpi-grid cols-4">${rows.map(([l, v]) => `<div class="kpi-card"><div class="kpi-label">${l}</div><div class="kpi-value">${v}</div></div>`).join('')}</div>`);
    }

    if (d.aging) {
      body += section('Inadimplência por Aging', table(
        ['Faixa', 'Valor em aberto'],
        [
          ['A vencer', formatCurrency(d.aging.a_vencer || 0)],
          ['1 a 30 dias', formatCurrency(d.aging.d1_30 || 0)],
          ['31 a 60 dias', formatCurrency(d.aging.d31_60 || 0)],
          ['61 a 90 dias', formatCurrency(d.aging.d61_90 || 0)],
          ['Acima de 90 dias', formatCurrency(d.aging.d90_plus || 0)],
          ['<strong>Total em aberto</strong>', `<strong>${formatCurrency(d.aging.total || 0)}</strong>`],
        ],
        [1]
      ));
    }

    if (d.by_payment_method?.length) {
      body += section('Por Forma de Pagamento', table(
        ['Método', 'Qtd.', 'Valor'],
        d.by_payment_method.map((r: any) => [esc(r.method), formatNumber(r.count), formatCurrency(r.amount)]),
        [1, 2]
      ));
    }

    if (d.by_category?.length) {
      body += section('Por Categoria', table(
        ['Categoria', 'Qtd.', 'Valor'],
        d.by_category.map((r: any) => [esc(r.category), formatNumber(r.count), formatCurrency(r.amount)]),
        [1, 2]
      ));
    }

    if (d.by_status?.length) {
      body += section('Por Status', table(
        d.by_status[0]?.amount !== undefined ? ['Status', 'Qtd.', 'Valor'] : ['Status', 'Qtd.'],
        d.by_status.map((r: any) => d.by_status[0]?.amount !== undefined
          ? [statusPill(r.status), formatNumber(r.count), formatCurrency(r.amount)]
          : [statusPill(r.status), formatNumber(r.count)]),
        [1, 2]
      ));
    }

    if (d.by_professional?.length) {
      body += section('Por Profissional', table(
        ['Profissional', 'Total', 'Concluídos', 'Faltas', 'Conclusão', ...(d.by_professional[0]?.revenue !== undefined ? ['Receita'] : [])],
        d.by_professional.map((r: any) => [
          esc(r.professional_name),
          formatNumber(r.total ?? r.total_appointments ?? 0),
          formatNumber(r.completed ?? r.completed_appointments ?? 0),
          formatNumber(r.no_show ?? r.no_shows ?? 0),
          formatPct(r.completion_rate),
          ...(r.revenue !== undefined ? [formatCurrency(r.revenue)] : []),
        ]),
        [1, 2, 3, 4, 5]
      ));
    }

    if (d.professionals?.length) {
      body += section('Produtividade por Profissional', table(
        ['Profissional', 'Especialidade', 'Atend.', 'Concluídos', 'Faltas', 'Conclusão', 'Ticket', 'Receita'],
        d.professionals.map((p: any) => [
          `<strong>${esc(p.professional_name)}</strong>`, esc(p.specialty || '—'),
          formatNumber(p.total_appointments), formatNumber(p.completed_appointments),
          formatNumber(p.no_shows), formatPct(p.completion_rate),
          formatCurrency(p.avg_ticket), `<strong>${formatCurrency(p.revenue)}</strong>`,
        ]),
        [2, 3, 4, 5, 6, 7]
      ));
    }

    if (d.top_procedures?.length) {
      body += section('Top Procedimentos', table(
        ['Procedimento', 'Orçados', 'Executados', 'Conversão', 'Receita orçada'],
        d.top_procedures.map((p: any) => [esc(p.name), formatNumber(p.budgeted_qty), formatNumber(p.executed_qty), formatPct(p.conversion_rate), formatCurrency(p.budgeted_revenue)]),
        [1, 2, 3, 4]
      ));
    }

    if (d.top_debtors?.length) {
      body += section('Maiores Devedores', table(
        ['Paciente', 'Cobranças', 'Total devido'],
        d.top_debtors.map((p: any) => [esc(p.patient_name), formatNumber(p.count), `<span class="red bold">${formatCurrency(p.total)}</span>`]),
        [1, 2]
      ));
    }

    if (d.by_insurance?.length) {
      body += section('Convênios', table(
        ['Convênio', 'Pacientes', 'Receita'],
        d.by_insurance.map((r: any) => [esc(r.name), formatNumber(r.patients), formatCurrency(r.revenue)]),
        [1, 2]
      ));
    }

    if (d.low_stock?.length) {
      body += section('Estoque Abaixo do Mínimo', table(
        ['Item', 'Estoque atual', 'Estoque mínimo'],
        d.low_stock.map((i: any) => [esc(i.name), `<span class="red bold">${i.current_stock}</span>`, i.minimum_stock]),
        [1, 2]
      ));
    }

    if (d.top_consumed?.length) {
      body += section('Mais Consumidos', table(
        ['Item', 'Quantidade', 'Custo total'],
        d.top_consumed.map((i: any) => [esc(i.item_name), formatNumber(i.quantity), formatCurrency(i.cost)]),
        [1, 2]
      ));
    }

    if (d.by_city?.length) {
      body += section('Pacientes por Cidade', table(
        ['Cidade', 'Quantidade'],
        d.by_city.map((r: any) => [esc(r.city), formatNumber(r.count)]),
        [1]
      ));
    }

    if (d.by_source?.length) {
      body += section('Origem dos Pacientes', table(
        ['Origem', 'Quantidade'],
        d.by_source.map((r: any) => [esc(r.source), formatNumber(r.count)]),
        [1]
      ));
    }

    if (d.by_age_group?.length) {
      body += section('Faixa Etária', table(
        ['Faixa etária', 'Quantidade'],
        d.by_age_group.map((r: any) => [esc(r.group), formatNumber(r.count)]),
        [1]
      ));
    }
  }

  const footer = `
    <div class="print-footer">
      <span>${clinicName}</span>
      <span>Gerado em ${now}${reportNumber ? ' &nbsp;·&nbsp; ' + esc(reportNumber) : ''}</span>
    </div>`;

  return `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8"><title>${esc(title)} — ${clinicName}</title>
<style>${PRINT_CSS}</style>
</head><body>
  ${header}
  ${body || '<p style="color:#64748b;text-align:center;padding:32px">Sem dados disponíveis para o período selecionado.</p>'}
  ${footer}
  <script>window.onload=()=>setTimeout(()=>window.print(),350);</script>
</body></html>`;
}

export default Reports;
